import { useEffect, useState } from "react";
import { api, resolveImage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";

const empty = {
    title: { de: "", tr: "", en: "" },
    description: { de: "", tr: "", en: "" },
    category_slug: "",
    image_url: "",
    price: "",
    old_price: "",
    unit: "",
    badge: "",
    valid_from: "",
    valid_until: "",
    featured: false,
    active: true,
};

export default function AdminOffers() {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [q, setQ] = useState("");

    const load = () => api.get("/admin/offers").then((r) => setItems(r.data));
    useEffect(() => {
        load();
        api.get("/admin/categories").then((r) => setCategories(r.data));
    }, []);

    const startCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
    const startEdit = (it) => {
        setEditing(it);
        setForm({
            title: it.title || { de: "", tr: "", en: "" },
            description: it.description || { de: "", tr: "", en: "" },
            category_slug: it.category_slug || "",
            image_url: it.image_url || "",
            price: it.price ?? "",
            old_price: it.old_price ?? "",
            unit: it.unit || "",
            badge: it.badge || "",
            valid_from: it.valid_from || "",
            valid_until: it.valid_until || "",
            featured: !!it.featured,
            active: it.active !== false,
        });
        setOpen(true);
    };

    const save = async (e) => {
        e.preventDefault();
        const payload = {
            ...form,
            price: form.price === "" ? null : parseFloat(form.price),
            old_price: form.old_price === "" ? null : parseFloat(form.old_price),
            valid_from: form.valid_from || null,
            valid_until: form.valid_until || null,
            category_slug: form.category_slug || null,
        };
        try {
            if (editing) {
                await api.put(`/admin/offers/${editing.id}`, payload);
                toast.success("Aktualisiert");
            } else {
                await api.post("/admin/offers", payload);
                toast.success("Angelegt");
            }
            setOpen(false);
            load();
        } catch (err) {
            toast.error("Fehler beim Speichern");
        }
    };

    const del = async (it) => {
        if (!confirm(`„${it.title?.de || "Eintrag"}" wirklich löschen?`)) return;
        await api.delete(`/admin/offers/${it.id}`);
        toast.success("Gelöscht");
        load();
    };

    const filtered = items.filter((it) => (it.title?.de || "").toLowerCase().includes(q.toLowerCase()));

    return (
        <div className="p-6 md:p-10 max-w-7xl">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] font-bold uppercase tracking-widest text-brand-green">Angebote</div>
                    <h1 className="mt-1 font-display text-3xl font-bold">Wochenangebote verwalten</h1>
                </div>
                <button onClick={startCreate} data-testid="offer-create" className="btn-primary"><Plus className="h-4 w-4" />Neues Angebot</button>
            </div>

            <div className="mt-6 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-charcoal/40" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen…" className="input-clean pl-9 max-w-sm" data-testid="offer-search" />
            </div>

            <div className="mt-6 card-clean overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-brand-cream-dark/50 text-left text-xs uppercase tracking-widest text-brand-charcoal/70">
                        <tr>
                            <th className="px-4 py-3">Bild</th>
                            <th className="px-4 py-3">Titel (DE)</th>
                            <th className="px-4 py-3">Kategorie</th>
                            <th className="px-4 py-3">Preis</th>
                            <th className="px-4 py-3">Featured</th>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((it) => (
                            <tr key={it.id} className="border-t border-black/5" data-testid={`offer-row-${it.id}`}>
                                <td className="px-4 py-3"><div className="h-10 w-10 rounded-lg bg-brand-cream-dark overflow-hidden">{it.image_url && <img src={resolveImage(it.image_url)} className="h-full w-full object-cover" alt="" />}</div></td>
                                <td className="px-4 py-3 font-medium">{it.title?.de}</td>
                                <td className="px-4 py-3 text-brand-charcoal/70">{it.category_slug || "—"}</td>
                                <td className="px-4 py-3"><span className="font-semibold text-brand-red">{it.price ? `€${it.price.toFixed(2)}` : "—"}</span>{it.old_price && <span className="ml-2 line-through text-brand-charcoal/40 text-xs">€{it.old_price.toFixed(2)}</span>}</td>
                                <td className="px-4 py-3">{it.featured ? <span className="text-brand-green font-semibold">Ja</span> : "—"}</td>
                                <td className="px-4 py-3 text-right">
                                    <button onClick={() => startEdit(it)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg hover:bg-brand-cream-dark" data-testid={`offer-edit-${it.id}`}><Pencil className="h-4 w-4" /></button>
                                    <button onClick={() => del(it)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg hover:bg-brand-cream-dark text-brand-red" data-testid={`offer-delete-${it.id}`}><Trash2 className="h-4 w-4" /></button>
                                </td>
                            </tr>
                        ))}
                        {filtered.length === 0 && (
                            <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-brand-charcoal/60">Noch keine Angebote.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>{editing ? "Angebot bearbeiten" : "Neues Angebot"}</DialogTitle></DialogHeader>
                    <form onSubmit={save} className="space-y-4">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-widest text-brand-charcoal/60 mb-2">Bild</div>
                            <ImageUploader value={form.image_url} onChange={(v) => setForm((f) => ({ ...f, image_url: v }))} />
                        </div>
                        <MultilangField label="Titel" value={form.title} onChange={(v) => setForm((f) => ({ ...f, title: v }))} />
                        <MultilangField label="Beschreibung" value={form.description} onChange={(v) => setForm((f) => ({ ...f, description: v }))} multiline />
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Preis (€)"><input type="number" step="0.01" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} className="input-clean" data-testid="offer-price" /></Field>
                            <Field label="Alter Preis (€)"><input type="number" step="0.01" value={form.old_price} onChange={(e) => setForm((f) => ({ ...f, old_price: e.target.value }))} className="input-clean" /></Field>
                            <Field label="Einheit"><input value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} className="input-clean" placeholder="kg, 500g, Stück" /></Field>
                            <Field label="Badge"><input value={form.badge} onChange={(e) => setForm((f) => ({ ...f, badge: e.target.value }))} className="input-clean" placeholder="-20%, NEU" /></Field>
                            <Field label="Kategorie">
                                <select value={form.category_slug} onChange={(e) => setForm((f) => ({ ...f, category_slug: e.target.value }))} className="input-clean">
                                    <option value="">—</option>
                                    {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name?.de}</option>)}
                                </select>
                            </Field>
                            <Field label="Gültig bis"><input type="date" value={form.valid_until?.slice(0, 10) || ""} onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))} className="input-clean" /></Field>
                        </div>
                        <div className="flex items-center gap-6 text-sm">
                            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} /> Featured</label>
                            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} /> Aktiv</label>
                        </div>
                        <DialogFooter className="pt-2">
                            <button type="button" className="btn-outline-dark" onClick={() => setOpen(false)}>Abbrechen</button>
                            <button type="submit" className="btn-primary" data-testid="offer-save">Speichern</button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export function Field({ label, children }) {
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-brand-charcoal/60 mb-2">{label}</label>
            {children}
        </div>
    );
}

export function MultilangField({ label, value, onChange, multiline = false }) {
    const langs = [["de", "DE"], ["tr", "TR"], ["en", "EN"]];
    return (
        <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-brand-charcoal/60 mb-2">{label}</div>
            <div className="grid sm:grid-cols-3 gap-2">
                {langs.map(([k, l]) => (
                    <div key={k}>
                        <div className="text-[10px] font-mono uppercase text-brand-charcoal/40 mb-1">{l}</div>
                        {multiline ? (
                            <textarea rows={2} value={value?.[k] || ""} onChange={(e) => onChange({ ...value, [k]: e.target.value })} className="input-clean text-sm" />
                        ) : (
                            <input value={value?.[k] || ""} onChange={(e) => onChange({ ...value, [k]: e.target.value })} className="input-clean text-sm" />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
